import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { isJwtExpired, storedToken } from '@domains/shared/domain/utils/jwt';
import { MediaAccessService } from '../infrastructure/system/media-access.service';

/** Nejkratší rozestup dvou obnov, v milisekundách. */
export const MEDIA_ACCESS_REFRESH_THROTTLE_MS = 60_000;

/**
 * Obnoví cookie k firemním obrázkům a videím, když se karta znovu ukáže (EL-2301).
 *
 * Cookie platí 12 h a server ji obnovuje na každém přihlášeném požadavku na API.
 * Karta schovaná přes noc ale žádný požadavek neposílá — po návratu by video spuštěné
 * nebo líně načtený obrázek odrolovaný dřív, než přijde první požadavek (ping času
 * čtení až po 30 s), dostalo 404 a obrázek se sám znovu nenačte. Proto jeden lehký
 * požadavek hned, jak je karta vidět.
 *
 * - jen přihlášenému s nevypršelým tokenem — jinak by to byla jistá 401,
 * - nejvýš jednou za minutu — přepínání mezi kartami nemá sypat požadavky,
 * - chyba se polyká: je to vedlejší kolej a uživatel o ní neví.
 */
@Injectable({ providedIn: 'root' })
export class MediaAccessRefreshService implements OnDestroy {

  private lastRefreshAt = Number.NEGATIVE_INFINITY;

  private started = false;

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.refresh();
    }
  };

  constructor(
    private mediaAccess: MediaAccessService,
    private zone: NgZone,
  ) { }

  /** Začne hlídat návrat do karty. Volá se jednou, z kořene aplikace. */
  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    // Mimo zónu: přepnutí karty samo o sobě nemá spouštět change detection.
    this.zone.runOutsideAngular(() => document.addEventListener('visibilitychange', this.onVisibilityChange));
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.started = false;
  }

  refresh(now: number = Date.now()): void {
    const token = storedToken();

    if (!token || isJwtExpired(token, now)) {
      return;
    }

    if (now - this.lastRefreshAt < MEDIA_ACCESS_REFRESH_THROTTLE_MS) {
      return;
    }

    this.lastRefreshAt = now;

    this.mediaAccess.refresh(token).subscribe({ error: () => undefined });
  }
}
